import {
  PopoverPosition as BlueprintPopoverPosition,
  Intent,
} from '@blueprintjs/core';
import {
  AddFilled,
  DataVis_1 as DataVis1,
  FlowConnection,
  Menu,
  Network_4 as Network4,
  Redo,
  Search,
  StrategyPlay,
  TrashCan,
  Undo,
  WarningAltFilled,
} from '@carbon/icons-react';
import classNames from 'classnames';
import * as go from 'gojs';
import * as React from 'react';
import {
  Input,
  InputButton,
  Tab,
  Tabs,
  Tooltip,
} from 'vet-bones/design-system/components';
import { isNotEqual } from 'vet-bones/utils';

import { withConfirmation } from 'src/components/withConfirmation';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectDiagramModelPopover } from 'src/containers/ProjectDiagram/components/modelPopover/ProjectDiagramModelPopover';
import { ProjectDiagramToolbarButton } from 'src/containers/ProjectDiagram/components/toolbar/ProjectDiagramToolbarButton';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useRemoveSelection } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveSelection';
import { useDiagramSelection } from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useIsLargeModel } from 'src/containers/ProjectDiagram/hooks/useIsLargeModel';
import { useYjsUndoManager } from 'src/containers/ProjectDiagram/hooks/useYjsUndoManager';
import { redrawLayout } from 'src/containers/ProjectDiagram/templates/extensions/layouts/utils/redrawLayout';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isModelConceptClass } from 'src/containers/ProjectDiagram/utils/data';
import { getQueryBuilderModel } from 'src/containers/ProjectDiagram/utils/getQueryBuilderModel';
import { getDiagramPartLabel } from 'src/containers/ProjectDiagram/utils/labels';
import { isRuleValid } from 'src/containers/ProjectDiagram/utils/rules/isRuleValid';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectDiagram/components/toolbar/projectDiagramToolbar.module.scss';

export const ProjectDiagramToolbar: React.VFC = () => {
  const { projectId } = useRouterParams();
  const activeDiagram = useDiagram();
  // The toolbar's canvas controls (undo/redo, selection, search) only make sense
  // against a real diagram, so narrow away the headless list view arm up front.
  const diagram =
    activeDiagram && !activeDiagram.headless ? activeDiagram : undefined;
  const { canRedo, canUndo, redo, undo } = useYjsUndoManager(diagram);
  const dispatch = useReduxDispatch();
  const selection = useDiagramSelection();
  const handleDeleteSelection = useRemoveSelection();

  const isModelListOpen = useReduxSelector(
    (state) => state.ui.modelList.isOpen
  );
  const isLargeModel = useIsLargeModel();

  const handleOpenListView = React.useCallback(() => {
    if (isModelListOpen) {
      return;
    }

    dispatch(
      uiSlice.actions.toggledModelList({
        forceDiagram: false,
        isOpen: true,
        projectId,
      })
    );
  }, [dispatch, isModelListOpen, projectId]);

  const handleOpenGraphView = React.useCallback(() => {
    // Already on the graph — don't re-prompt the large-model confirmation.
    if (!isModelListOpen) {
      return;
    }

    const openGraph = () =>
      dispatch(
        uiSlice.actions.toggledModelList({
          forceDiagram: true,
          isOpen: false,
          projectId,
        })
      );

    if (isLargeModel) {
      const { largeModelGraphConfirmation } =
        copy.components.projectDiagram.navbar;
      withConfirmation({
        message: largeModelGraphConfirmation.message,
        noButtonIntent: Intent.SUCCESS,
        noPrompt: largeModelGraphConfirmation.cancel,
        onConfirmationSuccess: openGraph,
        title: largeModelGraphConfirmation.title,
        yesButtonIntent: Intent.NONE,
        yesPrompt: largeModelGraphConfirmation.confirm,
      });
      return;
    }

    openGraph();
  }, [dispatch, isLargeModel, isModelListOpen, projectId]);

  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));

  const [searchValue, setSearchValue] = React.useState('');
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  const handleRedrawLayout = () => {
    if (!diagram) {
      return;
    }

    commitDiagramModel(diagram, redrawLayout, {
      layout: DiagramModelCommitLayout.FULL,
    });
  };

  const handleAddClass = () => {
    dispatch(
      uiSlice.actions.toggledCreateClassDrawer({
        isOpen: true,
      })
    );
  };

  const isSelectionExactlyTwoClasses = React.useMemo(() => {
    if (selection.size !== 2) {
      return false;
    }
    let isAllClasses = true;
    selection.each((part) => {
      if (!isModelConceptClass(part.data)) {
        isAllClasses = false;
      }
    });
    return isAllClasses;
  }, [selection]);

  // disable relationship button if there are more than 2 selections or if both selections are not classes
  const isRelationshipDisabled = React.useMemo(() => {
    if (isModelListOpen) {
      return false;
    }

    if (selection.size > 2) {
      return true;
    }
    if (selection.size < 2) {
      const currentSelection = selection.first();
      if (currentSelection) {
        return !isModelConceptClass(currentSelection.data);
      }
      return false;
    }
    if (selection.size === 2) {
      return !isSelectionExactlyTwoClasses;
    }

    return false;
  }, [isModelListOpen, isSelectionExactlyTwoClasses, selection]);

  const handleAddRelationship = () => {
    if (isModelListOpen) {
      dispatch(
        uiSlice.actions.toggledCreateRelationshipDrawer({
          isOpen: true,
        })
      );
      return;
    }

    const currentSelectedItem = selection.size > 0 ? selection.first() : null;
    const selectedData = currentSelectedItem?.data;

    if (
      selection.size === 1 &&
      selectedData &&
      isModelConceptClass(selectedData)
    ) {
      dispatch(
        uiSlice.actions.toggledCreateRelationshipDrawer({
          isOpen: true,
          sourceClassIri: selectedData.value,
        })
      );
    } else if (selection.size === 2 && isSelectionExactlyTwoClasses) {
      // preference for the diagram.selection because useDiagramSelection can be out of order
      const firstSelection =
        diagram?.selection?.first()?.part?.data.value ||
        selection.first()?.data.value;
      const secondSelection = selection
        .filter((part) => part.data.value !== firstSelection)
        .first()?.data?.value;

      dispatch(
        uiSlice.actions.toggledCreateRelationshipDrawer({
          isOpen: true,
          sourceClassIri: firstSelection,
          targetClassIri: secondSelection,
        })
      );
    } else {
      dispatch(
        uiSlice.actions.toggledCreateRelationshipDrawer({
          isOpen: true,
        })
      );
    }
  };

  const hasInvalidRules = React.useMemo(() => {
    if (!diagram) {
      return false;
    }

    const rules = Object.values(diagram.model.modelData.rulesById);
    if (!rules.length) {
      return false;
    }

    const ruleModel = getQueryBuilderModel(diagram.model);
    return rules.some((rule) => !isRuleValid(rule, ruleModel));
  }, [diagram]);

  const hasModifiedRules = React.useMemo(() => {
    if (!diagram) {
      return false;
    }

    const { draftData, rulesById } = diagram.model.modelData;
    if (!draftData) {
      return false;
    }

    return isNotEqual(draftData.prevModelData.rulesById, rulesById);
  }, [diagram]);

  const handleRules = () => {
    dispatch(uiSlice.actions.toggledRuleOverviewDrawer({ isOpen: true }));
  };

  const updateSelection = (value: string) => {
    if (!diagram) {
      return;
    }

    if (!value.trim()) {
      commitDiagramWhileSkippingUndoManager(
        diagram,
        (_, diagram) => setDiagramSelection(new go.Set())(diagram),
        DiagramTransaction.SET_DIAGRAM_SELECTION
      );
      return;
    }

    const keysToSelect: go.Set<go.Part> = new go.Set();

    const stringSearch = (label: string) => {
      return label?.toLowerCase().includes(value.trim().toLowerCase());
    };

    diagram.nodes.each((item) => {
      const label = getDiagramPartLabel(labelsByIri, item);

      if (stringSearch(label) || stringSearch(item.data.text)) {
        keysToSelect.add(item);
      }
    });

    diagram.links.each((item) => {
      const label = getDiagramPartLabel(labelsByIri, item);

      if (stringSearch(label) || stringSearch(item.data.text)) {
        keysToSelect.add(item);
      }
    });

    commitDiagramWhileSkippingUndoManager(
      diagram,
      (_, diagram) => setDiagramSelection(keysToSelect)(diagram),
      DiagramTransaction.SET_DIAGRAM_SELECTION
    );
  };

  const leftButtons = (
    <div
      className={classNames(styles.toolbarButtons, {
        [styles.diagramToolbarBackground]: !isModelListOpen,
      })}
    >
      <ProjectDiagramToolbarButton
        disabled={isModelListOpen}
        leftIcon={DataVis1}
        onClick={handleRedrawLayout}
        testId={TEST_IDS.projectDiagram.navbar.redrawLayout}
        tooltip={copy.components.projectDiagram.navbar.redrawLayout}
      />
      <ProjectDiagramToolbarButton
        leftIcon={AddFilled}
        onClick={handleAddClass}
        testId={TEST_IDS.projectDiagram.navbar.addClass}
        tooltip={copy.components.projectDiagram.navbar.addClass}
      />
      <ProjectDiagramToolbarButton
        disabled={isRelationshipDisabled}
        leftIcon={FlowConnection}
        onClick={handleAddRelationship}
        testId={TEST_IDS.projectDiagram.navbar.addRelationship}
        tooltip={copy.components.projectDiagram.navbar.addRelationship}
      />
      <ProjectDiagramToolbarButton
        disabled={isModelListOpen || !selection.size}
        leftIcon={TrashCan}
        onClick={handleDeleteSelection}
        testId={TEST_IDS.projectDiagram.navbar.deleteSelection}
        tooltip={copy.components.projectDiagram.navbar.deleteSelection}
      />
      <ProjectDiagramToolbarButton
        disabled={!canUndo}
        leftIcon={Undo}
        onClick={undo}
        testId={TEST_IDS.projectDiagram.navbar.undo}
        tooltip={copy.components.projectDiagram.navbar.undo}
      />
      <ProjectDiagramToolbarButton
        disabled={!canRedo}
        leftIcon={Redo}
        onClick={redo}
        testId={TEST_IDS.projectDiagram.navbar.redo}
        tooltip={copy.components.projectDiagram.navbar.redo}
      />
    </div>
  );

  return (
    <div
      className={classNames(styles.toolbar, {
        [styles.diagramToolbarPosition]: !isModelListOpen,
        [styles.listToolbarPosition]: isModelListOpen,
      })}
    >
      <div className={styles.toolbarSection}>
        <ProjectDiagramModelPopover />
        {leftButtons}
      </div>

      <div className={styles.toolbarSection}>
        <div
          className={classNames(styles.toolbarButtons, {
            [styles.diagramToolbarBackground]: !isModelListOpen,
          })}
        >
          {!isModelListOpen ? (
            <>
              {isSearchOpen ? (
                <Input
                  autoFocus
                  clearTestId={TEST_IDS.projectDiagram.navbar.searchClear}
                  label=""
                  minimal={false}
                  onBlur={() => {
                    if (!searchValue.trim()) {
                      setSearchValue('');
                      setIsSearchOpen(false);
                    }
                  }}
                  onChange={(evt) => {
                    setSearchValue(evt.target.value);
                    updateSelection(evt.target.value);
                  }}
                  onClear={() => {
                    setSearchValue('');
                    updateSelection('');
                    setIsSearchOpen(false);
                  }}
                  placeholder={
                    copy.components.projectDiagram.navbar.searchPlaceholder
                  }
                  rightElement={
                    !searchValue ? (
                      <>
                        <div className="vb-input-divider" />
                        <InputButton
                          icon={Search}
                          onClick={() => {
                            setIsSearchOpen(false);
                          }}
                          testId={TEST_IDS.projectDiagram.navbar.searchButton}
                        />
                      </>
                    ) : undefined
                  }
                  size="sm"
                  testId={TEST_IDS.projectDiagram.navbar.searchInput}
                  value={searchValue}
                />
              ) : (
                <ProjectDiagramToolbarButton
                  leftIcon={Search}
                  onClick={() => {
                    setIsSearchOpen(true);
                  }}
                  testId={TEST_IDS.projectDiagram.navbar.searchButton}
                  tooltip={copy.components.projectDiagram.navbar.search}
                />
              )}
            </>
          ) : null}
          <ProjectDiagramToolbarButton
            className={classNames('sd-draft-highlight-candidate', {
              'sd-draft-highlight-candidate--highlighted': hasModifiedRules,
            })}
            leftIcon={StrategyPlay}
            onClick={handleRules}
            rightIcon={hasInvalidRules ? WarningAltFilled : undefined}
            testId={TEST_IDS.projectDiagram.navbar.rules}
          >
            {copy.components.projectDiagram.navbar.rules}
          </ProjectDiagramToolbarButton>

          <Tabs size="md" variant="square">
            <Tooltip
              description={copy.components.projectDiagram.navbar.modelList}
              position={BlueprintPopoverPosition.TOP}
            >
              <Tab
                active={isModelListOpen}
                leftIcon={Menu}
                onClick={handleOpenListView}
                size="sm"
                testId={TEST_IDS.projectDiagram.navbar.listViewButton}
              />
            </Tooltip>
            <Tooltip
              description={copy.components.projectDiagram.navbar.modelGraph}
              position={BlueprintPopoverPosition.TOP}
            >
              <Tab
                active={!isModelListOpen}
                leftIcon={Network4}
                onClick={handleOpenGraphView}
                size="sm"
                testId={TEST_IDS.projectDiagram.navbar.graphViewButton}
              />
            </Tooltip>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
